import React, { Component, ReactNode } from 'react';
import { FetchResult } from '@apollo/client';
import { FormikActions, FormikTouched } from 'formik';
import url from 'url';

import AdditionalInformationContainer from './Pages/additionalInformation/AdditionalInformationContainer';
import ApplicantTypePageContainer from './Pages/applicantTypePage/ApplicantTypePageContainer';
import InformationContainer from './Pages/information/InformationContainer';
import ProApplicationSubmissionContainer from 'buyside/components/presentation/UserApplication/Pages/proApplicationSubmission/ProApplicationSubmissionContainer';
import ProSuccessPage from 'buyside/components/presentation/UserApplication/Pages/proSuccessPage/ProSuccessPage';
import Wizard from 'buyside/components/presentation/Form/Wizard/Wizard';

import {
  applicantTypePageSchema,
  applicationSubmissionSchema,
  basicInvestorTypePageSchema,
  informationSchema,
  proEmploymentStatusSchema
} from './Helpers/validations';

import {
  CreateOrUpdateUserApplicationMutation,
  CreateOrUpdateUserApplicationMutationFn,
  Maybe
} from 'interfaces/graphql-typings';
import { ApplicantTypes, ApplicationFormValues } from 'interfaces/userApplication/userApplication';

interface UserApplicationPageProps {
  applicantType?: Maybe<ApplicantTypes>;
  createOrUpdateUserApplication: CreateOrUpdateUserApplicationMutationFn;
  grecaptcha: () => Promise<unknown>;
  initialPage: number;
  initialValues: ApplicationFormValues;
  isLoading: boolean;
}
const { basic } = ApplicantTypes;

import styles from './userApplicationPageStyles.scss';

class UserApplicationPage extends Component<
  UserApplicationPageProps,
  {
    applicantType: Maybe<ApplicantTypes>;
    email: string;
    isRobot: boolean;
    shouldApproveRetail: boolean;
  }
> {
  constructor(props: UserApplicationPageProps) {
    super(props);
    this.state = {
      applicantType: this.props.applicantType || null, // Whether the application should be 'pro' or 'basic'
      isRobot: false,
      shouldApproveRetail: false,
      email: ''
    };
    this.handleApplicantType = this.handleApplicantType.bind(this);
    this.handleErrors = this.handleErrors.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.initialSubmit = this.initialSubmit.bind(this);
  }

  handleApplicantType(applicantType: ApplicantTypes): void {
    this.setState({
      applicantType
    });
  }

  handleErrors(bag: FormikActions<ApplicationFormValues>): void {
    bag.setSubmitting(false);
  }

  initialSubmit(
    values: ApplicationFormValues,
    setTouched: (touched: FormikTouched<ApplicationFormValues>) => void,
    setSubmitting: (isSubmitting: boolean) => void,
    next: (values: ApplicationFormValues) => void
  ): Promise<FetchResult<
    CreateOrUpdateUserApplicationMutation,
    Record<string, any>,
    Record<string, any>
  > | void> {
    const { grecaptcha, createOrUpdateUserApplication } = this.props;
    const {
      acceptedDisclaimer,
      applicantType,
      basicEmployerName,
      basicInvestorType,
      city,
      degree,
      email,
      employerAumRange,
      employerFundType,
      employerName,
      employmentStatus,
      expectedGraduation,
      fieldOfStudy,
      firstName,
      investorType,
      isAccredited,
      isStripeRegistered,
      lastName,
      linkedinUrl,
      phone,
      resume,
      schoolName,
      title,
      yearStart
    } = values;
    const referrerId = url.parse(location.pathname + location.search, true).query.referrer_id as
      | string
      | undefined;

    if (Object.values(values).includes(null)) {
      const untouchedFields = Object.keys(values).reduce(
        (acc: { [field: string]: boolean }, key) => {
          acc[key] = false;
          return acc;
        },
        {}
      );

      setTouched(untouchedFields);
    }

    return grecaptcha()
      .then((): Promise<FetchResult<
        CreateOrUpdateUserApplicationMutation,
        Record<string, any>,
        Record<string, any>
      > | void> | void => {
        if (!email) {
          this.setState({ isRobot: false });
          setSubmitting(false);
          return next(values);
        }

        return createOrUpdateUserApplication({
          variables: {
            acceptedDisclaimer,
            applicantType,
            basicEmployerName,
            basicInvestorType,
            city,
            degree,
            email,
            employerAumRange,
            employerFundType,
            employerName,
            employmentStatus,
            expectedGraduation,
            fieldOfStudy,
            firstName,
            investorType,
            isAccredited,
            isStripeRegistered,
            lastName,
            linkedinUrl,
            phone,
            referrerId,
            resume: resume ? JSON.stringify(resume) : null,
            schoolName,
            title,
            yearStart
          }
        });
      })
      .catch((): void => {
        setSubmitting(false);
        this.setState({ isRobot: true });
      });
  }

  handleSubmit(
    values: ApplicationFormValues,
    bag: FormikActions<ApplicationFormValues>,
    next: (values: ApplicationFormValues) => void
  ): void {
    const { email, isStripeRegistered } = values;
    this.initialSubmit(values, bag.setTouched, bag.setSubmitting, next).then(value => {
      if (!value) return;

      const userApp = value.data?.createOrUpdateUserApplication?.userApplication;
      if (userApp && email) {
        this.setState({ isRobot: false, email, shouldApproveRetail: Boolean(isStripeRegistered) });
        bag.setSubmitting(false);
        return next(values);
      } else {
        this.handleErrors(bag);
      }
    });
  }

  render(): ReactNode {
    const { applicantType, isRobot, shouldApproveRetail } = this.state;
    const { isLoading, initialValues, initialPage } = this.props;
    const additionalInformationContainerSchema =
      applicantType === basic ? basicInvestorTypePageSchema : proEmploymentStatusSchema;

    return (
      <div className={styles.main}>
        <div className={styles.left}>
          <h1>SumZero</h1>
          <hr />
          <h3>The world’s largest investment community</h3>
        </div>
        <div className={styles.right}>
          <div className={styles.rightContent}>
            <Wizard
              initialValues={initialValues}
              initialPage={initialPage}
              onSubmit={this.handleSubmit}
            >
              <ApplicantTypePageContainer
                handleApplicantType={this.handleApplicantType}
                isLoading={isLoading}
                isRobot={isRobot}
                validationSchema={applicantTypePageSchema}
              />
              <InformationContainer
                isLoading={isLoading}
                isRobot={isRobot}
                validationSchema={informationSchema}
              />
              <AdditionalInformationContainer
                applicantType={applicantType}
                isLoading={isLoading}
                isRobot={isRobot}
                validationSchema={additionalInformationContainerSchema}
              />
              <ProApplicationSubmissionContainer
                validationSchema={applicationSubmissionSchema}
                isLoading={isLoading}
                isRobot={isRobot}
                email={this.state.email}
                handleSubmit={this.initialSubmit}
              />
              {applicantType && (
                <ProSuccessPage
                  applicantType={applicantType}
                  isRetailApproved={Boolean(shouldApproveRetail)}
                />
              )}
            </Wizard>
          </div>
        </div>
      </div>
    );
  }
}

export default UserApplicationPage;
